/**
 * What the Disagree with this match card shows, worked out apart from the
 * screen so each rule can be tested on its own (STE-992).
 *
 * The server decides everything that matters: whether a re-match may run, what
 * it found, and whether Keep may go ahead. This only decides what to draw.
 */

import type { Benchmark, BenchmarkCut, FeedbackRecord, FeedbackView } from '../types'
import { closestOf } from './not-priced'

/**
 * The same limits the server holds in backend/src/lib/feedback.ts. Restated
 * because the browser does not import server code; the server refuses anything
 * outside them whatever the screen lets through.
 */
export const REASON_MIN = 10
export const REASON_MAX = 2000

/** Said before sending, in the same words the server would refuse with. */
export function reasonHint(reason: string): string | null {
  const text = reason.trim()
  if (text.length === 0) return 'Say why the match is wrong.'
  if (text.length < REASON_MIN) return `Say a little more about why the match is wrong: at least ${REASON_MIN} characters.`
  if (text.length > REASON_MAX) return `Keep the reason under ${REASON_MAX.toLocaleString('en-US')} characters.`
  return null
}

/** Only a role with a benchmark can be disagreed with. A failed run has nothing to disagree with yet. */
export const canDisagree = (bench: Pick<Benchmark, 'failedReason'> | null | undefined): boolean =>
  !!bench && !bench.failedReason

/**
 * What HR is disagreeing with: a match on a priced role, or the answer that a
 * role was not priced (STE-1063). Null where there is nothing to disagree with.
 *
 * A role that was not priced is the one HR most often disagrees with, so it
 * has the same option as a priced one, through the same re-match.
 */
export function disagreeKind(
  bench: Pick<Benchmark, 'failedReason' | 'noMatchReason'> | null | undefined,
): 'match' | 'answer' | null {
  if (!canDisagree(bench)) return null
  return bench!.noMatchReason !== null && bench!.noMatchReason !== undefined ? 'answer' : 'match'
}

/** The card's words. A priced role's read exactly as they always have. */
export function formWords(kind: 'match' | 'answer') {
  if (kind === 'match') {
    return {
      button: 'Disagree with this match',
      title: 'Disagree with this match',
      intro: 'Say why it is wrong. The role is matched again with your reason, under the same rules and the same survey, and you choose which answer to keep.',
      reasonLabel: 'Why this match is wrong',
      reasonPlaceholder: 'For example: this role runs payroll on its own, so an entry level job is too junior.',
      jobPlaceholder: 'Pick one from the Matches tab, or type a name',
      history: 'Feedback on this match',
    }
  }
  return {
    button: 'Disagree with this answer',
    title: 'Disagree with this answer',
    intro: 'Say what the matching missed, and suggest a survey job if you think one fits. The role is matched again with your reason, '
      + 'under the same rules and the same survey, and you choose which answer to keep. A job you suggest is checked like any other: '
      + 'if it still does not fit, you are told why and the role stays not priced.',
    reasonLabel: 'Why this answer is wrong',
    reasonPlaceholder: 'For example: this role is really program management, so GMA.02.001.M50 is close enough for us.',
    jobPlaceholder: 'Pick one of the closest jobs above, or type a name or code',
    history: 'Feedback on this answer',
  }
}

/**
 * The notice shown before a match picked by hand is put at risk.
 *
 * Shown above Send, and the request only says HR agreed when this was on the
 * screen, so a match nobody was warned about can never be replaced.
 */
export function consentNotice(bench: Pick<Benchmark, 'confirmed'>): string | null {
  if (!bench.confirmed) return null
  const d = new Date(bench.confirmed.at)
  const when = Number.isNaN(d.getTime())
    ? 'earlier'
    : `on ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`
  return `This match was picked by hand ${when}. Matching again may replace it.`
}

/** What is sent. `replaceConfirmed` only ever goes with a notice that was shown. */
export function askBody(reason: string, surveyJob: string, bench: Pick<Benchmark, 'confirmed'>) {
  const job = surveyJob.trim()
  return {
    reason: reason.trim(),
    ...(job ? { surveyJob: job } : {}),
    ...(consentNotice(bench) ? { replaceConfirmed: true } : {}),
  }
}

/** Which of the card's states to draw. */
export type FeedbackStage = 'none' | 'running' | 'ready' | 'failed' | 'stale'

export function stageOf(view: FeedbackView | null): FeedbackStage {
  const p = view?.pending
  if (!p) return 'none'
  if (p.state === 'queued' || p.state === 'matching') return 'running'
  return p.state
}

/** One side of the comparison. Null is a no match. */
export interface Side {
  code: string
  title: string
  median: number | null
  matchPercent: number | null
  managesPeople: boolean | null
}

const lead = (bench: Pick<Benchmark, 'cuts'>): BenchmarkCut | undefined =>
  bench.cuts.find((c) => c.chosen === true) ?? bench.cuts.find((c) => c.chosen !== false && (c.weight ?? 0) > 0)

export function sideOf(bench: Benchmark): Side | null {
  const cut = lead(bench)
  if (!cut || bench.noMatchReason !== null) return null
  return {
    code: cut.code,
    title: cut.title,
    median: bench.band?.p50 ?? null,
    matchPercent: typeof cut.score === 'number' ? Math.round(cut.score * 100) : null,
    managesPeople: bench.managesPeople,
  }
}

/** Current and new, and whether the re-match landed on the same survey job. */
export function comparison(current: Benchmark, next: Benchmark) {
  const now = sideOf(current)
  const then = sideOf(next)
  return {
    current: now, next: then, same: !!now && !!then && now.code === then.code,
    // Neither side priced: the re-match still found nothing close enough.
    stillUnpriced: !now && !then,
  }
}

/** The keep button, named for what it keeps. A new answer that priced nothing is not a match. */
export const keepNewWords = (c: { next: Side | null }): string =>
  c.next ? 'Keep the new match' : 'Keep the new answer'

/** The record, newest first, whatever order it arrived in. */
export const newestFirst = (history: FeedbackRecord[]): FeedbackRecord[] =>
  [...history].sort((a, b) => b.at.localeCompare(a.at))

/** What a decision reads as, in words. */
export const decisionWords: Record<FeedbackRecord['decision'], string> = {
  waiting: 'Waiting',
  'kept new': 'Kept the new match',
  'kept current': 'Kept the current one',
  failed: 'Could not be finished',
}

/**
 * Survey jobs already on the Matches tab whose title or code holds what was typed.
 *
 * On a role that was not priced there is no Matches tab, so the jobs that came
 * closest are offered instead, by code, the only name the matcher gave them.
 */
export function suggestions(
  bench: Pick<Benchmark, 'cuts'> & Partial<Pick<Benchmark, 'noMatchReason' | 'rejected'>>,
  typed: string, most = 6,
): string[] {
  const t = typed.trim().toLowerCase()
  if (t.length < 2) return []
  const onTab = bench.cuts
    .filter((c) => c.title.toLowerCase().includes(t) || c.code.toLowerCase().includes(t))
    .map((c) => c.title)
  const unpriced = bench.noMatchReason !== null && bench.noMatchReason !== undefined
  const closest = unpriced
    ? closestOf({ rejected: bench.rejected ?? [] }).map((r) => r.code).filter((code) => code.toLowerCase().includes(t))
    : []
  return [...onTab, ...closest].slice(0, most)
}

/** Every Api method this card uses, so a mock missing one is caught offline. */
export const FEEDBACK_METHODS = ['askFeedback', 'getFeedback', 'keepFeedback', 'discardFeedback'] as const
